import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse, cn, Scroller } from '@divlab/divanui';
import { IconChevronBottom } from '@divlab/divanui/icons';

import * as ModalWindows from '@Stores/ModalWindows';
import * as ConfiguratorQuery from '@Queries/Configurator';
import * as Filtrator from '@Stores/Filtrator';
import { useDeps } from '@Contexts/DI';
import useTranslation from '@Queries/useTranslation';
import AllFiltersButton from '../../../AllFiltersButton';
import Switcher from '../../../Switcher';
import styles from './ConfiguratorTab.module.css';

import type { ScrollState } from '@divlab/divanui';
import type { HTMLAttributes, ReactElement, MouseEvent } from 'react';
import type { ConfiguratorTab as ConfiguratorTabData } from '@Pages/PageProduct/typings';
import type { FiltersParameter, FiltersValue } from '@Types/Filters';

export interface ConfiguratorTabProps extends HTMLAttributes<HTMLDivElement> {
  tab: ConfiguratorTabData;
  entityId: Filtrator.FiltratorId;
  className?: string;
  slotFooter?: ReactElement;
  isActive: boolean;
  slotPositon?: 'sticky';
  renderSelectedSlot: (isActive: boolean) => ReactElement;
  onClickTab?: (tab: ConfiguratorTabData) => void;
}

interface SwitchChangeData {
  data: FiltersValue;
  parameter: FiltersParameter;
}

const ConfiguratorTab = (props: ConfiguratorTabProps) => {
  const {
    className,
    tab,
    children,
    slotFooter,
    renderSelectedSlot,
    isActive,
    entityId,
    slotPositon,
    onClickTab,
    ...restProps
  } = props;
  const [scrollPosition, setScrollPosition] = useState<ScrollState>(null);
  const [waitingCheckbox, setWaitingCheckbox] = useState(false);
  const filtrator = Filtrator.useFiltrator(entityId);
  const { applyFilters } = ConfiguratorQuery.useApplyFilters();
  const { changeFilters } = ConfiguratorQuery.useChangeFilters();
  const { resetFilters, isPending: isResetPending } = ConfiguratorQuery.useResetFilters();
  const { toggleInStock } = ConfiguratorQuery.useToggleInStock();
  const inStock = ConfiguratorQuery.useInStock();
  const { logger, analytics } = useDeps();
  const hasFilters = !!tab.filtersInfo || tab.hasItemsInStock;
  const { t } = useTranslation();

  const handleClickHeader = useCallback(() => {
    analytics.dispatchEvent('constructor.productPage.parameterTab', {
      action: !isActive ? 'open' : 'close',
      label: tab.title,
    });

    if (onClickTab) onClickTab(tab);
  }, [analytics, onClickTab, tab, isActive]);

  const handleScroll = useCallback((e: MouseEvent, state: ScrollState) => {
    setScrollPosition(state);
  }, []);

  const hasScroll = useMemo(() => {
    return scrollPosition && scrollPosition !== 'top' && scrollPosition !== 'none';
  }, [scrollPosition]);

  const handleToggleSwitch = useCallback(
    async (switchProps: SwitchChangeData) => {
      const { data, parameter } = switchProps;

      // Проверка на наличие параметра и значения
      if (!data || !parameter) {
        logger.log(`Invalid switchProps:${switchProps}`);
        return;
      }

      const opts = { id: data.parameterId, value: data.value };

      // Добавление или удаление чекбокса в зависимости от параметра
      const toggleCheckbox = parameter.default ? Filtrator.removeCheckbox : Filtrator.addCheckbox;
      toggleCheckbox(entityId, opts);

      // Отправка события аналитики, если параметр 'in_stock'
      if (data.parameterId === 'in_stock') {
        analytics.dispatchEvent('constructor.productPage.inStockFilter', {
          action: parameter.default ? 'discharge' : 'apply',
        });
      }

      setWaitingCheckbox(true);
      try {
        setTimeout(async () => {
          await changeFilters();
          await applyFilters();
        });
      } catch (error) {
        logger.log(error);
      } finally {
        Filtrator.applyFilters(entityId);
        setWaitingCheckbox(false);
      }
    },
    [analytics, applyFilters, changeFilters, entityId, logger],
  );

  const handleToggleStock = useCallback(() => {
    toggleInStock(!inStock);
  }, [inStock, toggleInStock]);

  const handleReset = useCallback(async () => {
    if (!isResetPending) await resetFilters();

    ModalWindows.close('Filters');
  }, [isResetPending, resetFilters]);

  const handleChangeFilters = useCallback(async () => {
    await changeFilters();
  }, [changeFilters]);

  const handleOpenFilters = useCallback(() => {
    ModalWindows.open('Filters', {
      selectedFilterId: 'all',
      entityId,
      maxWidth: 360,
      onApply: applyFilters,
      onReset: handleReset,
      onChangeFilters: handleChangeFilters,
      isOnProductPage: true,
    });
  }, [entityId, handleReset, handleChangeFilters, applyFilters]);

  useEffect(() => {
    if (!isActive) return;

    setTimeout(() => {
      analytics.dispatchEvent('constructor.productPage.parameterTab', {
        action: 'open',
        label: tab.title,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, tab.title]);
  return (
    <div {...restProps} className={cn(styles.tab, { [styles.collapsed]: !isActive }, className)}>
      <div
        className={cn(styles.header, {
          [styles.bordered]: hasScroll && !hasFilters,
        })}
        onClick={handleClickHeader}
      >
        <div className={cn(styles.headerContainer, { [styles.sticky]: slotPositon === 'sticky' })}>
          <div className={styles.name}>
            {tab.title}
            {typeof tab.count === 'number' && (
              <span className={styles.count}>{`(${tab.count})`}</span>
            )}
          </div>

          {!!renderSelectedSlot && (
            <div className={styles.selected}>{renderSelectedSlot(isActive)}</div>
          )}
          <div className={cn(styles.right, { [styles.rotated]: tab.view === 'modal' })}>
            <IconChevronBottom className={styles.chevron} />
          </div>
        </div>
      </div>
      <Collapse collapsed={!isActive} duration={200}>
        <div className={styles.wrapper}>
          {hasFilters && (
            <div className={cn(styles.container, { [styles.bordered]: hasScroll }, styles.filters)}>
              {!!tab.filtersInfo && <AllFiltersButton onClick={handleOpenFilters} />}

              {tab.filtersInfo &&
                filtrator.filters.map((filter) => {
                  if (filter.theme === 'switch') {
                    return filter.items.map((item) => {
                      const parameter = filtrator.parameters[item.parameterId];
                      const data = filtrator.parameterValues.find(
                        (val) => val.parameterId === item.parameterId,
                      );
                      const disabled = !data.count || waitingCheckbox;
                      const checked = !!parameter.default;

                      return (
                        item.theme === 'switch' && (
                          <Switcher
                            disabled={disabled}
                            checked={checked}
                            item={item}
                            notice={filter.notice}
                            name={parameter.name}
                            onToggle={() => handleToggleSwitch({ data, parameter })}
                          />
                        )
                      );
                    });
                  }
                })}

              {!tab.filtersInfo && tab.hasItemsInStock && (
                <Switcher
                  item={{ theme: 'switch', parameterId: 'in_stock' }}
                  notice={t('in-stock-switch.notice')}
                  name={t('in-stock-switch.title')}
                  checked={inStock}
                  onToggle={handleToggleStock}
                  preferredPopupPositions={['bottom-right']}
                />
              )}
            </div>
          )}

          <Scroller
            onChangeState={handleScroll}
            cnContainer={cn(styles.content, { [styles.withFilters]: hasFilters })}
          >
            <div className={cn(styles.container, { [styles.hasFooter]: !!slotFooter })}>
              {children}
            </div>
          </Scroller>
          {slotFooter && <div className={styles.slotFooter}>{slotFooter}</div>}
        </div>
      </Collapse>
    </div>
  );
};

export default memo(ConfiguratorTab);
